// Mirrors DB-backed authenticated workflow entry results for the current browser session only.
import type { OnboardingIntentRecord } from '@/modules/onboarding';

const STORAGE_KEY = 'sportbook.workflow.authenticatedIntentSession';

interface AuthenticatedWorkflowEntrySessionRecord {
  actorUserId: string | null;
  actorEmail: string | null;
  intentId: string;
  intentStatus: OnboardingIntentRecord['status'];
  nextRoute: string | null;
  cachedAt: string;
  source: 'db';
}

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function normalizeEmail(email: string | null | undefined): string | null {
  return email ? email.toLowerCase() : null;
}

function clearStorageValue(): void {
  if (!hasSessionStorage()) {
    return;
  }

  window.sessionStorage.removeItem(STORAGE_KEY);
}

function readStoredRecord(): AuthenticatedWorkflowEntrySessionRecord | null {
  if (!hasSessionStorage()) {
    return null;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthenticatedWorkflowEntrySessionRecord;
  } catch {
    clearStorageValue();
    return null;
  }
}

export function cacheAuthenticatedWorkflowIntent(input: {
  actorUserId?: string | null;
  actorEmail?: string | null;
  intent: Pick<OnboardingIntentRecord, 'id' | 'status'>;
  nextRoute?: string | null;
}): void {
  if (!hasSessionStorage()) {
    return;
  }

  const record: AuthenticatedWorkflowEntrySessionRecord = {
    actorUserId: input.actorUserId ?? null,
    actorEmail: normalizeEmail(input.actorEmail),
    intentId: input.intent.id,
    intentStatus: input.intent.status,
    nextRoute: input.nextRoute ?? null,
    cachedAt: new Date().toISOString(),
    source: 'db',
  };

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
}

export function readAuthenticatedWorkflowIntent(input: {
  actorUserId?: string | null;
  actorEmail?: string | null;
}): AuthenticatedWorkflowEntrySessionRecord | null {
  const record = readStoredRecord();
  if (!record) {
    return null;
  }

  const actorUserId = input.actorUserId ?? null;
  const actorEmail = normalizeEmail(input.actorEmail);
  const sameActor =
    (record.actorUserId && actorUserId && record.actorUserId === actorUserId) ||
    (record.actorEmail && actorEmail && record.actorEmail === actorEmail);

  if (!sameActor) {
    clearStorageValue();
    return null;
  }

  return record;
}

export function clearAuthenticatedWorkflowIntentCache(): void {
  clearStorageValue();
}

